import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setToken, getToken } from '@/lib/api';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = logged out, object = logged in
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      // Try cookie-only too (in case token wasn't saved but cookie still valid)
      try {
        const { data } = await api.get('/auth/me');
        setUser(data);
        return data;
      } catch {
        setUser(false);
        return null;
      }
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch {
      setToken(null);
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-leave convoys when user closes the tab (except admins).
  useEffect(() => {
    if (!user || user === false) return undefined;
    if (user.role === 'admin') return undefined;
    const handler = () => {
      try {
        const token = getToken();
        const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/convoys/leave-all';
        fetch(url, {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch (err) { console.warn('[Auth] leave-all on unload failed:', err); }
    };
    window.addEventListener('beforeunload', handler);
    window.addEventListener('pagehide', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      window.removeEventListener('pagehide', handler);
    };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.access_token) setToken(data.access_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post('/auth/register', { email, password, name });
    if (data.access_token) setToken(data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (err) { console.warn('[Auth] logout request failed:', err); }
    setToken(null);
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, login, register, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
